import { useCallback, useMemo, type ReactNode } from 'react';

import Text from '@cartwhl/ui/base/Text';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

/** White + hairline border; matches batch preview modal controls. */
const OUTLINE_WHITE_BUTTON =
  '!bg-white dark:!bg-gray-90 !border-gray-30 dark:!border-gray-75 border hover:!bg-gray-15 dark:hover:!bg-gray-75';

export type MascotVideoPreviewNavigationJob = {
  id: string;
  name: string;
  jobNumber: number;
};

type BatchMascotVideoPreviewModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle: string;
  jobNumber: number;
  totalJobs: number;
  videoUrl: string;
  onDownload: () => void;
  navigation: {
    jobs: MascotVideoPreviewNavigationJob[];
    onSelectJobId: (jobId: string) => void;
  };
  /** Modal shell width in px; defaults to batch-page layout (960). */
  modalMaxWidth?: number;
  /** Extra controls in the title row (e.g. asset library bookmark). */
  titleTrailing?: ReactNode;
  /** Extra controls beside Close in the footer (e.g. move to trash). */
  footerLeadingExtra?: ReactNode;
};

export default function BatchMascotVideoPreviewModal({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  jobNumber,
  totalJobs,
  videoUrl,
  onDownload,
  navigation,
  modalMaxWidth = 960,
  titleTrailing,
  footerLeadingExtra,
}: BatchMascotVideoPreviewModalProps) {
  const ordered = navigation.jobs;

  const goAdjacent = useCallback(
    (delta: -1 | 1) => {
      if (ordered.length <= 1) return;
      const i = ordered.findIndex((j) => j.id === jobId);
      if (i < 0) return;
      const next = (i + delta + ordered.length) % ordered.length;
      navigation.onSelectJobId(ordered[next]!.id);
    },
    [ordered, jobId, navigation],
  );

  const displayTitle =
    totalJobs <= 1
      ? jobTitle.trim() || 'scene'
      : `${String(jobNumber).padStart(2, '0')}. ${jobTitle.trim() || 'scene'}`;

  const modalTitle = useMemo(
    () => (
      <div className="flex w-full min-w-0 items-center justify-between gap-3">
        <Text
          as="div"
          variant="studio/title/md"
          className="text-primary min-w-0 flex-1 truncate text-start text-[20px]/[20px]"
        >
          {displayTitle}
        </Text>
        {totalJobs > 1 ? (
          <div className="flex shrink-0 items-center gap-3">
            <Text
              as="span"
              variant="studio/body/md"
              className="text-gray-60 whitespace-nowrap"
            >
              {jobNumber} of {totalJobs}
            </Text>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
              disabled={ordered.length <= 1}
              icon={<ChevronLeftIcon className="h-5 w-5" />}
              aria-label="Previous scene"
              onClick={() => goAdjacent(-1)}
            />
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
              disabled={ordered.length <= 1}
              icon={<ChevronLeftIcon className="h-5 w-5 rotate-180" aria-hidden />}
              aria-label="Next scene"
              onClick={() => goAdjacent(1)}
            />
          </div>
        ) : null}
        {titleTrailing}
      </div>
    ),
    [displayTitle, goAdjacent, jobNumber, ordered.length, titleTrailing, totalJobs],
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={modalTitle}
      maxWidth={modalMaxWidth}
      fixedHeightContent={false}
      fullscreenOnMobile
      horizontalBleed
      verticalBleed
      titleClassName="!mb-3 !px-3 !pt-3 lg:!mb-4 lg:!px-4 lg:!pt-4"
    >
      <div className="flex flex-col gap-3 px-3 pb-3 lg:gap-4 lg:px-4 lg:pb-4">
        <div
          className={`border-primary bg-secondary dark:bg-gray-90 relative aspect-video w-full shrink-0 overflow-hidden rounded-xl border${modalMaxWidth < 960 ? ' mx-auto max-w-2xl' : ''}`}
        >
          <video
            key={videoUrl}
            src={videoUrl}
            controls
            autoPlay
            playsInline
            crossOrigin="anonymous"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 gap-y-2 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className={OUTLINE_WHITE_BUTTON}
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            {footerLeadingExtra}
          </div>
          <Button
            type="button"
            variant="primary"
            size="lg"
            alignIcon="end"
            icon={<DownloadIcon className="h-5 w-5" />}
            onClick={onDownload}
          >
            Download
          </Button>
        </div>
      </div>
    </Modal>
  );
}
